/**
 * Reads `cognito:groups` off the stored ID token to determine platform role.
 *
 * Shared by both apps: the admin app's AdminGuard uses it as (a client-side
 * UX affordance only — see that file) route gate, and the user app's
 * Sidebar uses it only to decide whether to render the "Admin console" link
 * at all. Neither use is a security boundary — the real gate is server-side
 * on every /admin/* endpoint.
 */

export type PlatformRole =
  | "platform_admin"
  | "platform_support"
  | "platform_analyst";

const PLATFORM_ROLES: readonly PlatformRole[] = [
  "platform_admin",
  "platform_support",
  "platform_analyst",
];

function decodeIdTokenPayload(): Record<string, unknown> | null {
  const idToken = sessionStorage.getItem("spark_id_token");
  if (!idToken) return null;
  try {
    const [, payloadB64] = idToken.split(".");
    return JSON.parse(atob(payloadB64));
  } catch (_err) {
    // A malformed/absent token carries no claims to read — deliberate, not
    // an error worth surfacing (same decision getGroupsFromToken's own
    // malformed-token test already covers for the role claim).
    return null;
  }
}

function getGroupsFromToken(): string[] {
  const payload = decodeIdTokenPayload();
  if (!payload) return [];

  const groups = payload["cognito:groups"];
  if (Array.isArray(groups)) return groups;
  if (typeof groups === "string")
    return groups.split(",").map((g: string) => g.trim());
  return [];
}

/**
 * The signed-in user's own email, read the same way `getGroupsFromToken`
 * reads roles — straight off the stored ID token, no network call. Used by
 * the admin console's Platform roles UI to disable removing your own
 * `platform_admin` role before the request even reaches the server (which
 * enforces the same rule regardless — see admin/users/roles.ts). Returns
 * `null` for a missing or malformed token, same as `getPlatformRole`.
 */
export function getCurrentUserEmail(): string | null {
  const payload = decodeIdTokenPayload();
  const email = payload?.email;
  return typeof email === "string" ? email : null;
}

/** Whether the current user has any platform role. */
export function isAdmin(): boolean {
  const groups = getGroupsFromToken();
  return groups.some((g) => (PLATFORM_ROLES as readonly string[]).includes(g));
}

/** The user's highest-precedence platform role, or null. */
export function getPlatformRole(): PlatformRole | null {
  const groups = getGroupsFromToken();
  for (const role of PLATFORM_ROLES) {
    if (groups.includes(role)) return role;
  }
  return null;
}

/** Whether the user has at least one of the specified roles. */
export function hasRole(...roles: PlatformRole[]): boolean {
  const groups = getGroupsFromToken();
  return roles.some((r) => groups.includes(r));
}
